import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { GraduationCap, ArrowRight } from "lucide-react";
import { useTheme } from "@/theme";
import { COLLEGE_STATUS } from "@/types/enums";
import { ServerTimeProvider, useServerTime } from "@/hooks/useServerTime";
import { normalizeCollegeCode } from "@/utils/collegeCode";
import { formatISTDate, getServerTimeIST } from "@/utils/timezoneUtils";
import { getRegistrationAvailability } from "@/utils/registrationAvailability";

function HomePageContent() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { theme } = useTheme();
  const { offset } = useServerTime();
  const [selectedCollege, setSelectedCollege] = useState<string>("");

  const { data: colleges = [] } = useQuery({
    queryKey: ["colleges-public"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("colleges")
        .select("id, name, college_code, drive_start_date, drive_end_date")
        .eq("is_deleted", false)
        .eq("is_enabled", true)
        .eq("status", COLLEGE_STATUS.ACTIVE)
        .order("name");

      if (error) throw error;
      return data || [];
    },
  });

  const currentServerDate = useMemo(() => {
    if (offset === null) {
      return null;
    }

    return formatISTDate(getServerTimeIST(offset));
  }, [offset]);

  const availableColleges = useMemo(
    () =>
      colleges.filter(
        (college) => getRegistrationAvailability(college, currentServerDate).status === "open"
      ),
    [colleges, currentServerDate]
  );

  const hasSelectedCollege = availableColleges.some(
    (college) => college.id === selectedCollege
  );

  const handleRegisterForInterview = () => {
    if (!selectedCollege) {
      toast({
        title: "College Required",
        description: "Please select a college to register.",
        variant: "destructive",
      });
      return;
    }

    const college = availableColleges.find((c) => c.id === selectedCollege);
    if (college?.college_code) {
      navigate(`/register/${encodeURIComponent(normalizeCollegeCode(college.college_code))}`);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-4xl w-full">
        <div className="text-center mb-12 space-y-4">
          <div className="flex justify-center">
            <img src={theme.logo} alt="Logo" className="h-12 w-auto" />
          </div>
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              Welcome to HR Portal
            </h1>
            <p className="text-xl text-gray-600">
              Your gateway to career opportunities and assessments
            </p>
          </div>
        </div>

        <div className="mx-auto max-w-xl">
          {/* Register for Interview Card */}
          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader className="text-center">
              <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <GraduationCap className="h-6 w-6 text-green-600" />
              </div>
              <CardTitle className="text-xl">Register for Interview</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600 text-center">
                Start your application process by selecting your college.
              </p>
              <div>
                <Label htmlFor="college">Select Your College</Label>
                <Select
                  value={hasSelectedCollege ? selectedCollege : ""}
                  onValueChange={setSelectedCollege}
                >
                  <SelectTrigger disabled={availableColleges.length === 0}>
                    <SelectValue
                      placeholder={
                        availableColleges.length === 0
                          ? "No registrations open right now"
                          : "Choose college..."
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {availableColleges.map((college) => (
                      <SelectItem key={college.id} value={college.id}>
                        {college.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="mt-2 text-xs text-gray-500">
                  Only colleges within their registration window are shown here.
                </p>
              </div>
              <Button
                onClick={handleRegisterForInterview}
                className="w-full flex items-center gap-2"
                disabled={!hasSelectedCollege}
              >
                Start Registration
                <ArrowRight className="h-4 w-4" />
              </Button>
              {availableColleges.length === 0 ? (
                <p className="text-sm text-center text-amber-700 bg-amber-50 rounded-md px-3 py-2">
                  There are no active registration windows at the moment. Please
                  check again later or contact your placement coordinator.
                </p>
              ) : null}
            </CardContent>
          </Card>
        </div>

        <div className="text-center mt-8">
          <p className="text-sm text-gray-500">
            Need help? Contact your placement coordinator or HR team.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function HomePage() {
  return (
    <ServerTimeProvider>
      <HomePageContent />
    </ServerTimeProvider>
  );
}
